import React from 'react';
import {
  prettyField as prettyCompareField,
  buildModifiedRows as buildModifiedRowsExt,
} from '../utils/compareDrawer';

/**
 * CompareViewBody — the financial-statement variance view shared by the
 * inline Compare drawer (BudgetOverview.js) and the standalone pop-out
 * page (BudgetComparePopout.js).
 *
 * Pure presentational. No fetching, no routing, no useState. The caller
 * provides:
 *   - The /api/budget/versions/compare response (compareData)
 *   - Version + dept + production identifiers for the header band
 *   - formatMoney + formatDateShort (so VAT mode + locale are caller-controlled)
 *   - Collapse state for Cost Codes / Labour / Unchanged + their toggle callbacks
 *
 * Sign convention is enforced upstream by the server's cost_delta. This
 * component just renders the numbers it's handed; never reinterpret a
 * positive/negative here.
 */
export default function CompareViewBody({
  compareData,
  currentVersionName,
  comparisonVersionName,
  productionName,
  departmentName,
  companyName,
  formatMoney,
  formatDateShort,
  costCodesExpanded,
  onToggleCostCodes,
  labourExpanded,
  onToggleLabour,
  unchangedExpanded,
  onToggleUnchanged,
  // Optional JSX injection slots used by the standalone pop-out page
  // (BudgetComparePopout) to hoist its identity caption + dark-mode
  // toggle + Download/Print into the header band. The inline drawer
  // doesn't pass these — it keeps its own floating action group.
  //   headerExtraLeft        — caption, sits under the title on the left
  //   headerToolsBeforeTotals — dark-mode toggle, sits LEFT of the totals strip
  //   headerToolsAfterTotals  — Download/Print, sits RIGHT of the totals strip
  headerExtraLeft = null,
  headerToolsBeforeTotals = null,
  headerToolsAfterTotals = null,
}) {
  // Just the version name — "Current" is rendered as a separate
  // small caption next to it on the column header + totals strip
  // (sub-heading style), so we don't prefix it into the string here.
  const headingA = currentVersionName || 'Current';
  const headingB = comparisonVersionName || 'Comparison';

  // ── Pure helpers bound to the runtime formatters ────────────────────
  const cmpFmt = { formatMoney, formatDateShort };
  const buildModifiedRows = (row) => buildModifiedRowsExt(row, cmpFmt);

  const impactToneClass = (tone) => (
    tone === 'red' ? 'text-[#b91c1c] dark:text-red-400 font-semibold'
      : tone === 'emerald' ? 'text-[#047857] dark:text-emerald-400 font-semibold'
        : 'text-gray-400 dark:text-gray-500'
  );
  const signedTone = (delta) => {
    const v = Number(delta) || 0;
    return v > 0 ? 'red' : v < 0 ? 'emerald' : 'mute';
  };
  const signedText = (delta) => {
    const v = Number(delta) || 0;
    const sign = v > 0 ? '+' : v < 0 ? '−' : '';
    return `${sign}${formatMoney(Math.abs(v))}`;
  };
  const renderImpactCell = (delta, keySuffix) => {
    const v = Number(delta) || 0;
    const tone = signedTone(v);
    return (
      <td key={`imp-${keySuffix}`} className={`px-3 py-1.5 text-right tabular-nums ${impactToneClass(tone)}`}>
        {signedText(v)}
      </td>
    );
  };

  const renderMemberDeltaPill = (memberDelta) => {
    const v = Number(memberDelta) || 0;
    const tone = v > 0 ? 'red' : v < 0 ? 'emerald' : 'gray';
    const cls = tone === 'red'
      ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
      : tone === 'emerald'
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
        : 'bg-gray-100 text-gray-700 dark:bg-slate-700/60 dark:text-gray-300';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
        {signedText(v)}
      </span>
    );
  };

  // ── Pull diff buckets ───────────────────────────────────────────────
  const cc = compareData?.cost_codes || {};
  const ccAdded = cc.only_in_a || [];
  const ccRemoved = cc.only_in_b || [];
  const ccModified = [...(cc.modified || [])].sort(
    (x, y) => Math.abs(Number(y.variance) || 0) - Math.abs(Number(x.variance) || 0),
  );
  const ccVariance = Number(cc?.total?.current_variance) || 0;
  const ccATotal = Number(cc?.total?.a_total) || 0;
  const ccBTotal = Number(cc?.total?.b_total) || 0;

  const crewAdded = compareData?.only_in_a || [];
  const crewRemoved = compareData?.only_in_b || [];
  const crewModified = [...(compareData?.modified || [])].sort((x, y) => {
    const xVar = Math.abs((Number(x.a?.budget_cost) || 0) - (Number(x.b?.budget_cost) || 0));
    const yVar = Math.abs((Number(y.a?.budget_cost) || 0) - (Number(y.b?.budget_cost) || 0));
    return yVar - xVar;
  });
  const labourVariance = Number(compareData?.cost_delta?.current_variance) || 0;
  const labourATotal = Number(compareData?.cost_delta?.a_total) || 0;
  const labourBTotal = Number(compareData?.cost_delta?.b_total) || 0;

  const grandVariance = ccVariance + labourVariance;
  const grandATotal = ccATotal + labourATotal;
  const grandBTotal = ccBTotal + labourBTotal;

  // ── Header band ─────────────────────────────────────────────────────
  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });
  const titleSuffix = productionName || companyName || '';
  const grandPillTone = grandVariance > 0
    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
    : grandVariance < 0
      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
      : 'bg-gray-100 text-gray-700 dark:bg-slate-700/60 dark:text-gray-300';

  // ── Row factories ───────────────────────────────────────────────────
  const sectionBandRow = (label, key) => (
    <tr key={key} className="bg-[#2c4870] text-white">
      <td colSpan={4} className="px-3 py-2 text-sm font-bold uppercase tracking-wider">
        {label}
      </td>
    </tr>
  );

  const collapsibleSectionBandRow = (label, expanded, onToggle, key) => (
    <tr
      key={key}
      className="bg-[#2c4870] text-white cursor-pointer hover:bg-[#34527d] select-none"
      onClick={onToggle}
      role="button"
      aria-expanded={expanded}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <td colSpan={4} className="px-3 py-2 text-sm font-bold uppercase tracking-wider">
        <span className="inline-flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          {label}
        </span>
      </td>
    </tr>
  );

  // Subsection heading row — sits one level below the navy section band
  // (COST CODES / LABOUR) but clearly above the data rows. Slate fill with
  // a left accent bar so the bucket boundary reads at a glance. Bg is
  // intentionally darker than both the white data rows AND the subtle
  // dark-mode zebra (slate-700/25) so the heading never blends in.
  const subheadingRow = (label, key) => (
    <tr key={key} className="bg-slate-200 dark:bg-slate-700">
      <td
        colSpan={4}
        className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-100 border-l-4 border-[#2c4870]"
      >
        {label}
      </td>
    </tr>
  );

  const emptyMessageRow = (msg, key) => (
    <tr key={key}>
      <td colSpan={4} className="px-3 py-2 text-xs italic text-gray-400 dark:text-gray-500">
        {msg}
      </td>
    </tr>
  );

  const subtotalRow = (label, aVal, bVal, impactVal, key) => (
    <tr key={key} className="bg-[#f0f0f0] dark:bg-slate-700 font-bold border-t-2 border-gray-400 dark:border-slate-500">
      <td className="px-3 py-1.5 text-gray-900 dark:text-gray-100">{label}</td>
      <td className="px-3 py-1.5 text-right tabular-nums text-gray-900 dark:text-gray-100">{formatMoney(aVal)}</td>
      <td className="px-3 py-1.5 text-right tabular-nums text-gray-900 dark:text-gray-100">{formatMoney(bVal)}</td>
      {renderImpactCell(impactVal, `subtot-${key}`)}
    </tr>
  );

  const grandRow = (label, aVal, bVal, impactVal, key) => {
    // Grand-total Impact uses LIGHTER red/emerald that stay readable on
    // navy. Matches the FP&A industry norm + the PDF's matching cell.
    const v = Number(impactVal) || 0;
    const grandImpactCls = v > 0
      ? 'text-red-300'
      : v < 0
        ? 'text-emerald-300'
        : 'text-white';
    return (
      <tr key={key} className="bg-[#1e3a5f] text-white font-bold border-t-[3px] border-[#1e3a5f]">
        <td className="px-3 py-2 uppercase tracking-wide">{label}</td>
        <td className="px-3 py-2 text-right tabular-nums">{formatMoney(aVal)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{formatMoney(bVal)}</td>
        <td className={`px-3 py-2 text-right tabular-nums ${grandImpactCls}`}>{signedText(impactVal)}</td>
      </tr>
    );
  };

  const ccDataRow = (row, aVal, bVal, impactVal, key) => (
    <tr key={key} className="even:bg-[#f8f8f8] dark:even:bg-slate-700/25 hover:bg-blue-50 dark:hover:bg-slate-700/40">
      <td className="px-3 py-1.5 pl-6">
        <span className="font-mono text-xs text-gray-500 dark:text-gray-400 mr-2">{row.code}</span>
        <span className="text-gray-800 dark:text-gray-200">{row.name}</span>
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-gray-800 dark:text-gray-200">
        {aVal == null ? <span className="text-gray-400 dark:text-gray-500">—</span> : formatMoney(aVal)}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-gray-800 dark:text-gray-200">
        {bVal == null ? <span className="text-gray-400 dark:text-gray-500">—</span> : formatMoney(bVal)}
      </td>
      {renderImpactCell(impactVal, key)}
    </tr>
  );

  const crewSummaryRow = (row, aVal, bVal, impactVal, grade, key) => (
    <tr key={key} className="even:bg-[#f8f8f8] dark:even:bg-slate-700/25 hover:bg-blue-50 dark:hover:bg-slate-700/40">
      <td className="px-3 py-1.5 pl-6">
        <span className="text-gray-800 dark:text-gray-200">{row.name}</span>
        {grade && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({grade})</span>}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-gray-800 dark:text-gray-200">
        {aVal == null ? <span className="text-gray-400 dark:text-gray-500">—</span> : formatMoney(aVal)}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums text-gray-800 dark:text-gray-200">
        {bVal == null ? <span className="text-gray-400 dark:text-gray-500">—</span> : formatMoney(bVal)}
      </td>
      {renderImpactCell(impactVal, key)}
    </tr>
  );

  const crewDetailRow = (subRow, parentKey, idx) => {
    const label = subRow.kind === 'slot' ? subRow.label : prettyCompareField(subRow.field);
    const toneCls = impactToneClass(subRow.impact?.tone || 'mute');
    return (
      <tr key={`${parentKey}-d${idx}`} className="even:bg-[#f8f8f8] dark:even:bg-slate-700/25">
        <td className="px-3 py-1 pl-8 text-xs italic text-gray-500 dark:text-gray-400">{label}</td>
        <td className="px-3 py-1 text-right text-xs tabular-nums text-gray-500 dark:text-gray-400 whitespace-nowrap">{subRow.aDisplay}</td>
        <td className="px-3 py-1 text-right text-xs tabular-nums text-gray-500 dark:text-gray-400 whitespace-nowrap">{subRow.bDisplay}</td>
        <td className={`px-3 py-1 text-right text-xs tabular-nums whitespace-nowrap ${toneCls}`}>{subRow.impact?.text || '—'}</td>
      </tr>
    );
  };

  return (
    <>
      {/* Header band — mirrors the PDF: title + production on the left,
          dept + date right-aligned on the same row, with a navy underline
          below. */}
      <div className="px-5 pt-4 pb-3 border-b-[3px] border-[#1e3a5f]">
        {/* pr-24 reserves a clear ~96px on the right so the inline Compare
            drawer's floating icon group (pop-out ↗ + close ✕, sitting at
            top-3 right-3 z-20 above this band) never overlaps the V1 / V2
            / Variance totals strip. The pop-out page has no overlay so
            the extra padding is harmless there. */}
        <div className={`flex items-start justify-between gap-4 ${(headerToolsBeforeTotals || headerToolsAfterTotals) ? '' : 'pr-24'}`}>
          <div className="min-w-0 flex-1">
            <h2 className="text-[#1e3a5f] dark:text-blue-200 font-bold leading-tight">
              <span className="text-lg">Variance Analysis</span>
              <span className="ml-2 text-base font-bold">({headingA} vs {headingB})</span>
              {titleSuffix && (
                <span className="ml-2 text-sm font-bold">— {titleSuffix.toUpperCase()}</span>
              )}
              {headerExtraLeft && (
                <>
                  <span className="mx-2 text-sm font-normal text-gray-400 dark:text-gray-500">-</span>
                  {headerExtraLeft}
                </>
              )}
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {(departmentName || 'Department').toUpperCase()} — {today.toUpperCase()}
            </p>
          </div>
          {compareData && (
            <div className="text-right text-xs whitespace-nowrap flex items-center gap-3 flex-shrink-0">
              {headerToolsBeforeTotals && (
                <div className="flex items-center gap-2">{headerToolsBeforeTotals}</div>
              )}
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
                  <span>
                    <span className="text-[9px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mr-1">Current</span>
                    {headingA}: <span className="font-semibold text-gray-900 dark:text-gray-100">{formatMoney(grandATotal)}</span>
                  </span>
                  <span>{headingB}: <span className="font-semibold text-gray-900 dark:text-gray-100">{formatMoney(grandBTotal)}</span></span>
                </div>
                <span
                  className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${grandPillTone}`}
                  title="Positive = current over baseline. Negative = current under baseline (savings)."
                >
                  Variance: {signedText(grandVariance)}
                </span>
              </div>
              {headerToolsAfterTotals && (
                <div className="flex items-center gap-2">{headerToolsAfterTotals}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main table */}
      {compareData && (
        <>
          <table className="w-full text-sm border-collapse [&_td]:border [&_td]:border-gray-300 [&_th]:border [&_th]:border-[#1e3a5f] dark:[&_td]:border-slate-600 dark:[&_th]:border-[#1e3a5f]">
            <thead className="sticky top-0 z-10 shadow-sm">
              <tr className="bg-[#1e3a5f] text-white">
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Item</th>
                <th className="px-3 py-1.5 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap">
                  <div className="text-[9px] font-normal uppercase tracking-wider text-blue-200/80 leading-tight">Current</div>
                  <div className="leading-tight">{headingA}</div>
                </th>
                <th className="px-3 py-2 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap">{headingB}</th>
                <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider whitespace-nowrap">Impact</th>
              </tr>
            </thead>
            <tbody>
              {/* ── COST CODES ──────────────────────────────────── */}
              {collapsibleSectionBandRow('Cost Codes', costCodesExpanded, onToggleCostCodes, 'sec-cc')}
              {costCodesExpanded && (
                <>
                  {ccAdded.length > 0 && (
                    <>
                      {subheadingRow(`Newly budgeted in ${headingA} (${ccAdded.length})`, 'sub-cc-add')}
                      {ccAdded.map(row => ccDataRow(row, row.a_budget || 0, null, row.a_budget || 0, `cc-add-${row.id}`))}
                    </>
                  )}
                  {ccRemoved.length > 0 && (
                    <>
                      {subheadingRow(`Budget removed in ${headingA} (${ccRemoved.length})`, 'sub-cc-rem')}
                      {ccRemoved.map(row => ccDataRow(row, null, row.b_budget || 0, -(row.b_budget || 0), `cc-rem-${row.id}`))}
                    </>
                  )}
                  {ccModified.length > 0 && (
                    <>
                      {subheadingRow(`Modified (${ccModified.length})`, 'sub-cc-mod')}
                      {ccModified.map(row => {
                        const v = Number(row.variance) || 0;
                        return ccDataRow(row, row.a_budget || 0, row.b_budget || 0, v, `cc-mod-${row.id}`);
                      })}
                    </>
                  )}
                  {(ccAdded.length + ccRemoved.length + ccModified.length === 0) &&
                    emptyMessageRow('No cost code budget changes between versions.', 'cc-empty')}
                </>
              )}
              {/* Subtotal stays visible even when collapsed. */}
              {subtotalRow('Total Cost Code Variance', ccATotal, ccBTotal, ccVariance, 'cc-subtot')}

              {/* ── LABOUR ──────────────────────────────────────── */}
              {collapsibleSectionBandRow('Labour', labourExpanded, onToggleLabour, 'sec-lab')}
              {labourExpanded && (
                <>
                  {crewAdded.length > 0 && (
                    <>
                      {subheadingRow(`Crew added in ${headingA} (${crewAdded.length})`, 'sub-lab-add')}
                      {crewAdded.map(row => crewSummaryRow(
                        row, row.budget_cost || 0, null, row.budget_cost || 0, row.grade_name,
                        `lab-add-${row.lineage_id}`,
                      ))}
                    </>
                  )}
                  {crewRemoved.length > 0 && (
                    <>
                      {subheadingRow(`Crew removed in ${headingA} (${crewRemoved.length})`, 'sub-lab-rem')}
                      {crewRemoved.map(row => crewSummaryRow(
                        row, null, row.budget_cost || 0, -(row.budget_cost || 0), row.grade_name,
                        `lab-rem-${row.lineage_id}`,
                      ))}
                    </>
                  )}
                  {crewModified.length > 0 && (
                    <>
                      {subheadingRow(`Modified (${crewModified.length})`, 'sub-lab-mod')}
                      {crewModified.map(row => {
                        const memberVar = (Number(row.a?.budget_cost) || 0) - (Number(row.b?.budget_cost) || 0);
                        const grade = row.a?.grade_name || row.b?.grade_name;
                        const parentKey = `lab-mod-${row.lineage_id}`;
                        const detailRows = buildModifiedRows(row);
                        return (
                          <React.Fragment key={parentKey}>
                            <tr className="even:bg-[#f8f8f8] dark:even:bg-slate-700/25 hover:bg-blue-50 dark:hover:bg-slate-700/40">
                              <td className="px-3 py-1.5 pl-6">
                                <span className="text-gray-800 dark:text-gray-200 font-medium">{row.name}</span>
                                {grade && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({grade})</span>}
                                <span className="ml-3">{renderMemberDeltaPill(memberVar)}</span>
                              </td>
                              <td className="px-3 py-1.5 text-right tabular-nums text-gray-800 dark:text-gray-200">{formatMoney(row.a?.budget_cost || 0)}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums text-gray-800 dark:text-gray-200">{formatMoney(row.b?.budget_cost || 0)}</td>
                              {renderImpactCell(memberVar, parentKey)}
                            </tr>
                            {detailRows.map((sub, i) => crewDetailRow(sub, parentKey, i))}
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}
                  {(crewAdded.length + crewRemoved.length + crewModified.length === 0) &&
                    emptyMessageRow('No labour changes between versions.', 'lab-empty')}
                </>
              )}
              {subtotalRow('Total Labour Variance', labourATotal, labourBTotal, labourVariance, 'lab-subtot')}

              {/* ── GRAND TOTAL ─────────────────────────────────── */}
              {grandRow('Grand Total Variance', grandATotal, grandBTotal, grandVariance, 'grand')}
            </tbody>
          </table>

          {/* Unchanged appendix — collapsible, default closed. */}
          <div className="border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onToggleUnchanged}
              aria-expanded={unchangedExpanded}
              className="w-full px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 bg-[#f5f6f8] dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700/60 flex items-center gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${unchangedExpanded ? 'rotate-90' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Unchanged
              <span className="text-gray-400 dark:text-gray-500 font-normal normal-case">
                ({(cc.unchanged || []).length + (compareData.unchanged || []).length})
              </span>
            </button>
            {unchangedExpanded && (
              <table className="w-full text-sm border-collapse [&_td]:border [&_td]:border-gray-300 dark:[&_td]:border-slate-600">
                <tbody>
                  {(cc.unchanged || []).length > 0 && (
                    <>
                      {sectionBandRow('Cost Codes (Unchanged)', 'unc-cc-sec')}
                      {(cc.unchanged || []).map(row => (
                        <tr key={`unc-cc-${row.id}`} className="even:bg-[#f8f8f8] dark:even:bg-slate-700/25">
                          <td className="px-3 py-1.5">
                            <span className="font-mono text-xs text-gray-500 dark:text-gray-400 mr-2">{row.code}</span>
                            <span className="text-gray-700 dark:text-gray-300">{row.name}</span>
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-gray-700 dark:text-gray-300" colSpan={3}>
                            {formatMoney(row.a_budget || 0)}
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                  {(compareData.unchanged || []).length > 0 && (
                    <>
                      {sectionBandRow('Labour (Unchanged)', 'unc-lab-sec')}
                      {(compareData.unchanged || []).map(row => (
                        <tr key={`unc-lab-${row.lineage_id}`} className="even:bg-[#f8f8f8] dark:even:bg-slate-700/25">
                          <td className="px-3 py-1.5">
                            <span className="text-gray-700 dark:text-gray-300">{row.name}</span>
                            {row.grade_name && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({row.grade_name})</span>}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-gray-700 dark:text-gray-300" colSpan={3}>
                            {formatMoney(row.budget_cost || 0)}
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                  {(cc.unchanged || []).length + (compareData.unchanged || []).length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-3 text-xs italic text-gray-500 dark:text-gray-400">
                        No unchanged items between versions.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </>
  );
}
